import { CreatableSelect, GroupBase, SelectComponentsConfig } from 'chakra-react-select';
import { generated } from '@varos/research-sdk-api';
import { assert, assertNever } from '@varos/util-typescript';
import {
    IconButton,
    Button,
    HStack,
    Icon,
    StackDivider,
    Text,
    Tooltip,
    VStack,
    Link as ChakraLink,
    Progress,
    ButtonProps,
    Tag,
    Box,
    Wrap,
    Input,
    InputGroup,
    Checkbox,
    Select,
    InputRightElement,
    NumberInput,
    NumberInputField,
    NumberIncrementStepper,
    NumberInputStepper,
    NumberDecrementStepper,
    Textarea,
    chakra,
    Spinner,
    Center,
} from '@chakra-ui/react';
import {
    BooleanElementViewProps,
    ElementComponents,
    EnumElementViewProps,
    NumberElementViewProps,
    ValueFieldViewProps,
} from '../../../../view';
import { ReportingViewBaseConfig } from '../../../../base';
import { TextElementViewProps } from './text';
import { DatetimeElementViewProps } from './datetime';
import { SnippetElementViewProps } from './snippet';
import React, { Suspense } from 'react';
import { SnippetInputComponents, SnippetInputContainerProps } from '../../../snippet';
import TextareaAutosize from 'react-textarea-autosize';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { OptionItemProps } from './enum';

const ChakraTextareaAutosize = chakra(TextareaAutosize, {
    baseStyle: {
        // any default Chakra styles you want here
        // e.g. fontSize: "md", etc.
    },
});

export function createValueFieldView(
    config: Omit<ReportingViewBaseConfig, 'Container'> & {
        Container: {
            Element: ElementComponents;
            Snippet: {
                Input: React.FC<SnippetInputContainerProps>;
            };
        };
    }
): React.FC<ValueFieldViewProps> {
    const {
        Container: {
            Element: {
                Text: TextElementContainer,
                Number: NumberElementContainer,
                Boolean: BooleanElementContainer,
                Datetime: DatetimeElementContainer,
                Enum: EnumElementContainer,
                Snippet: SnippetElementContainer,
            },
            Snippet: { Input: SnippetInputContainer },
        },
    } = config;

    const EnumOptionSelect = CreatableSelect<OptionItemProps>;

    const TextElement: React.FC<TextElementViewProps> = (props) => {
        if (props.edit.value === false) {
            return (
                <Button
                    {...props.edit.button}
                    bg="none"
                    w="full"
                    borderRadius="md"
                    fontWeight="medium"
                    justifyContent="start"
                    textAlign="left"
                    _focus={{ outline: 'none' }}
                    _focusVisible={{ outline: 'none' }}
                    _hover={{ bg: 'whiteAlpha.50' }}
                    {...(props.isEmpty
                        ? { color: 'whiteAlpha.400' }
                        : { color: 'whiteAlpha.800' })}
                >
                    {props.label}
                </Button>
            );
        }
        return (
            <InputGroup ref={props.containerRef}>
                <Input
                    {...props.input}
                    type="text"
                    fontSize="medium"
                    _focus={{ outline: 'none' }}
                    _focusVisible={{ outline: 'none' }}
                />
                <InputRightElement>
                    <HStack px={4}>
                        <IconButton
                            {...props.submit.button}
                            aria-label={props.submit.label}
                            h={5}
                            w={5}
                            minH={5}
                            minW={5}
                            borderRadius="full"
                            bg="whiteAlpha.900"
                            color="blackAlpha.900"
                            _disabled={{
                                opacity: 0.3,
                                cursor: 'normal',
                                _hover: {
                                    bg: 'whiteAlpha.900',
                                },
                            }}
                            _hover={{ bg: 'whiteAlpha.800' }}
                            _active={{ bg: 'whiteAlpha.900' }}
                            _focus={{ outline: 'none' }}
                            icon={<Icon as={props.submit.Icon} fontSize="sm" />}
                        />
                    </HStack>
                </InputRightElement>
            </InputGroup>
        );
    };

    const NumberElement: React.FC<NumberElementViewProps> = (props) => {
        if (props.edit.value === false) {
            return (
                <Button
                    {...props.edit.button}
                    bg="none"
                    w="full"
                    borderRadius="md"
                    fontWeight="medium"
                    justifyContent="start"
                    textAlign="left"
                    _focus={{ outline: 'none' }}
                    _focusVisible={{ outline: 'none' }}
                    _hover={{ bg: 'whiteAlpha.50' }}
                    {...(props.isEmpty
                        ? { color: 'whiteAlpha.400' }
                        : { color: 'whiteAlpha.800' })}
                >
                    {props.label}
                </Button>
            );
        }
        return (
            <InputGroup w="full" ref={props.containerRef}>
                <NumberInput
                    {...props.input}
                    w="full"
                    _focus={{ outline: 'none' }}
                    _focusVisible={{ outline: 'none' }}
                >
                    <NumberInputField />
                    <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                    </NumberInputStepper>
                    <InputRightElement px={3} mr={6}>
                        <IconButton
                            {...props.submit.button}
                            aria-label={props.submit.label}
                            h={5}
                            w={5}
                            minH={5}
                            minW={5}
                            borderRadius="full"
                            bg="whiteAlpha.900"
                            color="blackAlpha.900"
                            _disabled={{
                                opacity: 0.3,
                                cursor: 'normal',
                                _hover: {
                                    bg: 'whiteAlpha.900',
                                },
                            }}
                            _hover={{ bg: 'whiteAlpha.800' }}
                            _active={{ bg: 'whiteAlpha.900' }}
                            _focus={{ outline: 'none' }}
                            icon={<Icon as={props.submit.Icon} fontSize="sm" />}
                        />
                    </InputRightElement>
                </NumberInput>
            </InputGroup>
        );
    };

    const BooleanElement: React.FC<BooleanElementViewProps> = (props) => {
        return (
            <HStack
                w="full"
                minH={10}
                h={10}
                borderRadius="md"
                px={3}
                _hover={{ bg: 'whiteAlpha.50' }}
            >
                <Checkbox {...props.input} />
            </HStack>
        );
    };

    const DatetimeElement: React.FC<DatetimeElementViewProps> = (props) => {
        if (props.edit.value === false) {
            return (
                <Button
                    {...props.edit.button}
                    bg="none"
                    w="full"
                    borderRadius="md"
                    fontWeight="medium"
                    justifyContent="start"
                    textAlign="left"
                    _focus={{ outline: 'none' }}
                    _focusVisible={{ outline: 'none' }}
                    _hover={{ bg: 'whiteAlpha.50' }}
                    {...(props.isEmpty
                        ? { color: 'whiteAlpha.400' }
                        : { color: 'whiteAlpha.800' })}
                >
                    {props.label}
                </Button>
            );
        }
        return (
            <InputGroup ref={props.containerRef}>
                <Input
                    {...props.input}
                    type="datetime-local"
                    fontSize="medium"
                    _focus={{ outline: 'none' }}
                    _focusVisible={{ outline: 'none' }}
                />
                <InputRightElement>
                    <HStack px={4}>
                        <IconButton
                            {...props.submit.button}
                            aria-label={props.submit.label}
                            h={5}
                            w={5}
                            minH={5}
                            minW={5}
                            borderRadius="full"
                            bg="whiteAlpha.900"
                            color="blackAlpha.900"
                            _disabled={{
                                opacity: 0.3,
                                cursor: 'normal',
                                _hover: {
                                    bg: 'whiteAlpha.900',
                                },
                            }}
                            _hover={{ bg: 'whiteAlpha.800' }}
                            _active={{ bg: 'whiteAlpha.900' }}
                            _focus={{ outline: 'none' }}
                            icon={<Icon as={props.submit.Icon} fontSize="sm" />}
                        />
                    </HStack>
                </InputRightElement>
            </InputGroup>
        );
    };

    const EnumElementComponents: Partial<
        SelectComponentsConfig<OptionItemProps, false, GroupBase<OptionItemProps>>
    > = {
        MenuList(props) {
            return (
                <VStack
                    w="full"
                    align="start"
                    spacing={0}
                    bg="whiteAlpha.100"
                    borderRadius="md"
                    borderWidth={1}
                    borderColor="whiteAlpha.200"
                    fontSize="sm"
                    fontWeight="medium"
                    py={1}
                >
                    <HStack py={2} px={3} userSelect="none">
                        <Text fontWeight="semibold" fontSize="xs" color="whiteAlpha.500">
                            Select or create an option
                        </Text>
                    </HStack>
                    <VStack w="full" align="start" spacing={0}>
                        {props.children}
                    </VStack>
                </VStack>
            );
        },
        Option(props) {
            return (
                <HStack
                    px={1}
                    w="full"
                    onClick={props.selectOption.bind(null, props.data)}
                >
                    <HStack
                        w="full"
                        spacing={3}
                        px={3}
                        py={2}
                        color="whiteAlpha.800"
                        userSelect="none"
                        borderRadius="md"
                        {...(props.isFocused
                            ? {
                                  bg: 'whiteAlpha.100',
                                  _hover: { bg: 'whiteAlpha.100' },
                              }
                            : {
                                  _hover: { bg: 'whiteAlpha.50' },
                              })}
                    >
                        <Box
                            w={2}
                            h={2}
                            bg={`${props.data.colorScheme}.200`}
                            borderRadius="full"
                        />
                        <Text>{props.data.label}</Text>
                    </HStack>
                </HStack>
            );
        },
    };

    const EnumElement: React.FC<EnumElementViewProps> = (props) => {
        if (props.isEmpty && props.edit.value === false) {
            return (
                <Button
                    {...props.edit.button}
                    bg="none"
                    w="full"
                    borderRadius="md"
                    fontWeight="medium"
                    justifyContent="start"
                    textAlign="left"
                    _focus={{ outline: 'none' }}
                    _focusVisible={{ outline: 'none' }}
                    _hover={{ bg: 'whiteAlpha.50' }}
                    {...(props.isEmpty
                        ? { color: 'whiteAlpha.400' }
                        : { color: 'whiteAlpha.800' })}
                >
                    {props.label}
                </Button>
            );
        }
        if (!props.isEmpty && props.edit.value === false) {
            const colorScheme = props.value?.colorScheme || 'blue';

            return (
                <Button
                    {...props.edit.button}
                    bg="none"
                    w="full"
                    borderRadius="md"
                    fontWeight="medium"
                    color="whiteAlpha.600"
                    justifyContent="start"
                    textAlign="left"
                    _focus={{ outline: 'none' }}
                    _focusVisible={{ outline: 'none' }}
                    _hover={{ bg: 'whiteAlpha.50' }}
                >
                    <Wrap shouldWrapChildren={true}>
                        <Tag
                            variant="solid"
                            colorScheme={colorScheme}
                            size="md"
                            borderRadius="full"
                            px={3}
                        >
                            <HStack>
                                <Box
                                    w={2}
                                    h={2}
                                    bg={`whiteAlpha.400`}
                                    borderRadius="full"
                                />
                                <Text>{props.label}</Text>
                            </HStack>
                        </Tag>
                    </Wrap>
                </Button>
            );
        }
        return (
            <EnumOptionSelect
                {...props.input}
                defaultMenuIsOpen={true}
                tagVariant="subtle"
                components={EnumElementComponents}
                chakraStyles={{
                    container: (base) => ({
                        ...base,
                        w: 'full',
                    }),
                    control: (base) => ({
                        ...base,
                        _focusVisible: { outline: 'none' },
                    }),
                    dropdownIndicator: (base) => ({
                        ...base,
                        color: 'whiteAlpha.400',
                    }),
                    menu: (base) => ({
                        ...base,
                        bg: 'black',
                    }),
                }}
            />
        );
    };

    const SnippetComponents: SnippetInputComponents = {
        Root(props) {
            return (
                <Box {...props.element} w="full" h="full">
                    {props.children}
                </Box>
            );
        },
        Input(props) {
            return (
                <VStack
                    w="full"
                    align="start"
                    spacing={0}
                    borderWidth={1}
                    borderColor="whiteAlpha.200"
                    borderRadius="md"
                    divider={<StackDivider borderColor="whiteAlpha.200" />}
                >
                    <HStack fontSize="sm" justify="space-between" w="full" px={4} py={2}>
                        <Text
                            fontWeight="semibold"
                            color="whiteAlpha.800"
                            userSelect="none"
                        >
                            {props.label}
                        </Text>
                    </HStack>
                    <VStack
                        align="start"
                        w="full"
                        px={4}
                        py={2}
                        border="none"
                        bg="whiteAlpha.50"
                        position="relative"
                    >
                        <ChakraTextareaAutosize
                            {...props.textarea}
                            w="full"
                            bg="none"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck="false"
                            fontSize="sm"
                            color="whiteAlpha.800"
                            border="none"
                            minRows={3}
                            maxRows={10}
                            resize="none"
                            _focus={{ outline: 'none' }}
                            _focusVisible={{ outline: 'none' }}
                        />

                        <HStack w="full" justify="space-between" align="end">
                            <HStack flexShrink={0}>
                                <Text
                                    fontWeight="bold"
                                    color="whiteAlpha.400"
                                    textTransform="uppercase"
                                    fontSize="xs"
                                    userSelect="none"
                                >
                                    Plain text
                                </Text>
                            </HStack>
                            <HStack justify="end" w="full" spacing={2}>
                                {props.cancel && (
                                    <Button
                                        {...props.cancel.button}
                                        size="xs"
                                        bg="none"
                                        color="whiteAlpha.600"
                                        _hover={{ bg: 'whiteAlpha.50' }}
                                        _active={{ bg: 'whiteAlpha.100' }}
                                        _focus={{ outline: 'none' }}
                                        _focusVisible={{ outline: 'none' }}
                                    >
                                        Cancel
                                    </Button>
                                )}
                                <Button
                                    {...props.save.button}
                                    size="xs"
                                    bg="whiteAlpha.900"
                                    color="blackAlpha.900"
                                    _hover={{ bg: 'whiteAlpha.800' }}
                                    _active={{ bg: 'whiteAlpha.900' }}
                                    _focus={{ outline: 'none' }}
                                    _focusVisible={{ outline: 'none' }}
                                >
                                    {props.save.label}
                                </Button>
                            </HStack>
                        </HStack>
                    </VStack>
                </VStack>
            );
        },
        Placeholder(props) {
            return (
                <Button
                    {...props.button}
                    bg="none"
                    w="full"
                    borderRadius="md"
                    fontWeight="medium"
                    justifyContent="start"
                    textAlign="left"
                    _focus={{ outline: 'none' }}
                    _focusVisible={{ outline: 'none' }}
                    _hover={{ bg: 'whiteAlpha.50' }}
                    color="whiteAlpha.400"
                >
                    Empty
                </Button>
            );
        },
        Label(props) {
            return (
                <Button
                    {...props.button}
                    bg="none"
                    w="full"
                    borderRadius="md"
                    fontWeight="medium"
                    color="whiteAlpha.800"
                    _focus={{ outline: 'none' }}
                    _focusVisible={{ outline: 'none' }}
                    _hover={{ bg: 'whiteAlpha.50' }}
                >
                    <HStack w="full" justifyContent="space-between">
                        <Text noOfLines={1}>{props.content}</Text>
                        <Icon as={ChevronDownIcon} color="whiteAlpha.400" fontSize="lg" />
                    </HStack>
                </Button>
            );
        },
    };

    const SnippetElement: React.FC<SnippetElementViewProps> = (props) => {
        return (
            <HStack w="full" ref={props.containerRef} fontSize="sm">
                <Suspense
                    fallback={
                        <Center w="full" h="full" p={3}>
                            <HStack spacing={1} color="whiteAlpha.400">
                                <Text>Loading</Text>
                                <Spinner size="sm" speed="0.5s" />
                            </HStack>
                        </Center>
                    }
                >
                    <SnippetInputContainer as={SnippetComponents} {...props.field} />
                </Suspense>
            </HStack>
        );
    };

    // function getRatingColorScheme(props: {
    //     value: number;
    //     min: number;
    //     max: number;
    // }): ButtonProps['colorScheme'] {
    //     const { value, min, max } = props;
    //     const range = max - min;

    //     if (range === 0) {
    //         // Handle cases where min and max are the same, or max is less than min.
    //         // Default to green if value is at or above max, otherwise red.
    //         return value >= max ? 'green' : 'red';
    //     }

    //     const lowerBoundOrange = min + range / 3;
    //     const lowerBoundGreen = min + (2 * range) / 3;

    //     if (value >= lowerBoundGreen) {
    //         return 'green';
    //     }
    //     if (value >= lowerBoundOrange) {
    //         return 'orange';
    //     }
    //     return 'red';
    // }

    return (props) => {
        if (props.element.kind === 'text') {
            return <TextElementContainer as={TextElement} {...props.element} />;
        }
        if (props.element.kind === 'number') {
            return <NumberElementContainer as={NumberElement} {...props.element} />;
        }
        if (props.element.kind === 'boolean') {
            return <BooleanElementContainer as={BooleanElement} {...props.element} />;
        }
        if (props.element.kind === 'datetime') {
            return <DatetimeElementContainer as={DatetimeElement} {...props.element} />;
        }
        if (props.element.kind === 'enum') {
            return <EnumElementContainer as={EnumElement} {...props.element} />;
        }
        if (props.element.kind === 'snippet') {
            return <SnippetElementContainer as={SnippetElement} {...props.element} />;
        }
        assertNever(props.element);
        // if (props.element?.kind === 'rating' && config.compact === false) {
        //     const colorScheme = getRatingColorScheme(props.element);
        //     content = (
        //         <HStack spacing={3}>
        //             <Progress
        //                 bg="whiteAlpha.50"
        //                 w="full"
        //                 minW="12rem"
        //                 size="sm"
        //                 colorScheme={colorScheme}
        //                 // Add some minimum width
        //                 value={Math.max(props.element.value, props.element.min + 1)}
        //                 min={props.element.min}
        //                 max={props.element.max}
        //             />
        //             <Text
        //                 whiteSpace="nowrap"
        //                 fontWeight="bold"
        //                 fontSize="sm"
        //                 color={`${colorScheme}.200`}
        //             >
        //                 {props.element.value}
        //             </Text>
        //         </HStack>
        //     );
        // }
        // if (props.element?.kind === 'rating' && config.compact === true) {
        //     const colorScheme = getRatingColorScheme(props.element);
        //     content = (
        //         <Tag size="md" colorScheme={colorScheme}>
        //             <HStack spacing={3}>
        //                 <Text
        //                     whiteSpace="nowrap"
        //                     fontWeight="bold"
        //                     fontSize="sm"
        //                     color={`${colorScheme}.200`}
        //                 >
        //                     <Box as="span">{props.element.value}</Box>
        //                     <Box as="span"> / </Box>
        //                     <Box as="span">{props.element.max}</Box>
        //                 </Text>
        //             </HStack>
        //         </Tag>
        //     );
        // }
        // if (props.element?.kind === 'link') {
        //     content = (
        //         <HStack>
        //             <ChakraLink
        //                 whiteSpace="nowrap"
        //                 href={props.element.href}
        //                 isExternal={true}
        //                 onClick={(event) => {
        //                     event.stopPropagation();
        //                 }}
        //             >
        //                 {props.element.label}
        //             </ChakraLink>
        //         </HStack>
        //     );
        // }
        // if (props.element?.kind === 'relation' && props.element.cardinality === 'one') {
        //     return (
        //         <ChakraLink
        //             as={Link}
        //             to={props.element.link.to}
        //             onClick={(event) => {
        //                 event.stopPropagation();
        //             }}
        //         >
        //             <Tooltip
        //                 placement="top"
        //                 openDelay={300}
        //                 px={2}
        //                 py={1}
        //                 bg="gray.50"
        //                 borderRadius="md"
        //                 color="gray.900"
        //                 isDisabled={!config.tooltip}
        //                 shouldWrapChildren={true}
        //                 label={props.property.label}
        //             >
        //                 <HStack
        //                     spacing={1}
        //                     flexBasis={0}
        //                     textDecor="underline"
        //                     color="whiteAlpha.700"
        //                     _hover={{ color: 'whiteAlpha.800' }}
        //                 >
        //                     {props.element.icon.kind === 'emoji' && (
        //                         <Text>{props.element.icon.text}</Text>
        //                     )}
        //                     {props.element.icon.kind === 'icon' && (
        //                         <Icon fontSize="md" as={props.element.icon.Component} />
        //                     )}
        //                     <Text noOfLines={1} maxW={config.compact ? 32 : 64}>
        //                         {props.element.label}
        //                     </Text>
        //                 </HStack>
        //             </Tooltip>
        //         </ChakraLink>
        //     );
        // }
        // if (props.element?.kind === 'relation' && props.element.cardinality === 'many') {
        //     return (
        //         <Wrap spacing={1} shouldWrapChildren={true}>
        //             {props.element.items.map((item) => (
        //                 <ChakraLink
        //                     key={item.id}
        //                     as={Link}
        //                     to={item.link.to}
        //                     onClick={(event) => {
        //                         event.stopPropagation();
        //                     }}
        //                 >
        //                     <Tooltip
        //                         placement="top"
        //                         openDelay={300}
        //                         px={2}
        //                         py={1}
        //                         bg="gray.50"
        //                         borderRadius="md"
        //                         color="gray.900"
        //                         isDisabled={!config.tooltip}
        //                         shouldWrapChildren={true}
        //                         label={props.property.label}
        //                     >
        //                         <HStack
        //                             bg="whiteAlpha.200"
        //                             borderRadius="full"
        //                             py={1}
        //                             px={2}
        //                             spacing={2}
        //                             flexShrink={0}
        //                             _hover={{ bg: 'whiteAlpha.100' }}
        //                             {...(config.compact
        //                                 ? {
        //                                       h: 5,
        //                                       fontSize: 'sm',
        //                                   }
        //                                 : {
        //                                       h: 6,
        //                                       fontSize: 'md',
        //                                   })}
        //                         >
        //                             {!item.icon && (
        //                                 <Box
        //                                     w={2}
        //                                     h={2}
        //                                     borderRadius="full"
        //                                     bg="whiteAlpha.700"
        //                                 />
        //                             )}
        //                             {item.icon?.kind === 'emoji' && (
        //                                 <Text
        //                                     {...(config.compact
        //                                         ? {
        //                                               fontSize: 'sm',
        //                                           }
        //                                         : {
        //                                               fontSize: 'md',
        //                                           })}
        //                                 >
        //                                     {item.icon.text}
        //                                 </Text>
        //                             )}
        //                             <Text
        //                                 color="whiteAlpha.700"
        //                                 noOfLines={1}
        //                                 maxW={config.compact ? 32 : 64}
        //                             >
        //                                 {item.label}
        //                             </Text>
        //                         </HStack>
        //                     </Tooltip>
        //                 </ChakraLink>
        //             ))}
        //         </Wrap>
        //     );
        // }
        // return (
        //     <Tooltip
        //         {...props.tooltip}
        //         placement="top"
        //         openDelay={300}
        //         px={2}
        //         py={1}
        //         bg="gray.50"
        //         borderRadius="md"
        //         color="gray.900"
        //         isDisabled={!config.tooltip}
        //         shouldWrapChildren={true}
        //     >
        //         {content}
        //     </Tooltip>
        // );
    };
}
